import { motion } from "framer-motion";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Loader2,
  AlertCircle,
  FileText,
  Star,
  Calendar,
  User,
} from "lucide-react";

import {
  useBlogsAdmin,
  useDeleteBlog,
  type BlogPost,
} from "../../../services/blogService";
import type { RootState } from "../../../store";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent } from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

const Blogs: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState<BlogPost | null>(null);

  const queryParams = {
    per_page: 10,
    page: currentPage,
    admin: true,
    ...(searchTerm && { search: searchTerm }),
    ...(statusFilter && { status: statusFilter }),
  };

  const { data: blogsData, isLoading, error } = useBlogsAdmin(queryParams);
  const deleteBlogMutation = useDeleteBlog();

  const blogs = blogsData?.posts || [];
  const pagination = blogsData?.pagination;

  const handleDeleteBlog = (blog: BlogPost) => {
    setBlogToDelete(blog);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteBlog = async () => {
    if (!blogToDelete) return;

    try {
      await deleteBlogMutation.mutateAsync(Number(blogToDelete.id));
      setIsDeleteDialogOpen(false);
      setBlogToDelete(null);
    } catch (error) {
      console.error("Failed to delete blog:", error);
    }
  };

  const cancelDeleteBlog = () => {
    setIsDeleteDialogOpen(false);
    setBlogToDelete(null);
  };

  const handleEditBlog = (blog: BlogPost) => {
    navigate(`/${currentLanguage}/admin/blogs/${blog.id}/edit`);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "draft":
        return "outline";
      case "review":
        return "default";
      case "published":
        return "secondary";
      case "archived":
        return "destructive";
      default:
        return "outline";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString(currentLanguage, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              {t("admin.blogs.title", "Blog Posts")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t("admin.blogs.subtitle", "Manage your blog posts and articles")}
            </p>
          </div>
          <Link to={`/${currentLanguage}/admin/blogs/create`}>
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg">
              <Plus className="h-4 w-4 mr-2" />
              {t("admin.blogs.create", "Create Post")}
            </Button>
          </Link>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder={t("admin.blogs.search", "Search posts...")}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              >
                <option value="">
                  {t("admin.blogs.allStatuses", "All Statuses")}
                </option>
                <option value="draft">{t("admin.blogs.draft", "Draft")}</option>
                <option value="review">
                  {t("admin.blogs.review", "Review")}
                </option>
                <option value="published">
                  {t("admin.blogs.published", "Published")}
                </option>
                <option value="archived">
                  {t("admin.blogs.archived", "Archived")}
                </option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
                <p>
                  {t("admin.blogs.errorLoading", "Error loading blog posts")}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : blogs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {t("admin.blogs.noPosts", "No blog posts found")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid gap-4">
              {blogs.map((blog, index) => (
                <motion.div
                  key={blog.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Featured Image */}
                        {blog.featured_image && (
                          <div className="sm:w-32 sm:h-32 w-full h-48 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={blog.featured_image}
                              alt={blog.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                                {blog.title}
                              </h3>
                              {blog.excerpt && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                  {blog.excerpt}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {blog.is_featured && (
                                <Badge
                                  variant="secondary"
                                  className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                >
                                  <Star className="h-3 w-3 mr-1" />
                                  {t("admin.blogs.featured", "Featured")}
                                </Badge>
                              )}
                              <Badge
                                variant={getStatusBadgeVariant(blog.status)}
                              >
                                {blog.status}
                              </Badge>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-4">
                            {blog.author && (
                              <div className="flex items-center gap-1">
                                <User className="h-4 w-4" />
                                <span>{blog.author}</span>
                              </div>
                            )}
                            {blog.published_at && (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                <span>{formatDate(blog.published_at)}</span>
                              </div>
                            )}
                            {blog.read_time && (
                              <span>
                                {blog.read_time}{" "}
                                {t("admin.blogs.minRead", "min read")}
                              </span>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 mt-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditBlog(blog);
                              }}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              {t("admin.blogs.edit", "Edit")}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteBlog(blog);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t("admin.blogs.delete", "Delete")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
              <div className="flex justify-center items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t("admin.blogs.page", "Page")} {pagination.current_page}{" "}
                  {t("admin.blogs.of", "of")} {pagination.last_page}
                </span>
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(pagination.last_page, p + 1))
                  }
                  disabled={currentPage === pagination.last_page}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("admin.blogs.deleteConfirm", "Delete Blog Post")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "admin.blogs.deleteConfirmMessage",
                  "Are you sure you want to delete this blog post? This action cannot be undone."
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={cancelDeleteBlog}>
                {t("admin.blogs.cancel", "Cancel")}
              </Button>
              <Button variant="destructive" onClick={confirmDeleteBlog}>
                {t("admin.blogs.delete", "Delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Blogs;
